import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Copy, ExternalLink, Trophy } from 'lucide-react';
import TopDonateBoard from '../../../components/top-donate/top-donate-board';
import {
  DEFAULT_TOP_DONATE_OPTIONS,
  TOP_DONATE_BY,
  TOP_DONATE_MAX,
  TOP_DONATE_MIN,
  TOP_DONATE_RANGES,
  TOP_DONATE_STYLES,
  TopDonateOptions,
  TopDonateRow,
  loadTopDonate,
  topDonateQuery,
  topDonateTitle,
} from '../../../components/top-donate/top-donate-options';

// Nền giả lập khung hình live để thấy rõ bảng trông ra sao khi đè lên video.
const STAGE_BG = 'linear-gradient(135deg, #0B1220 0%, #1F2937 60%, #2B3A55 100%)';

const SELECT_CLASS =
  'h-12 w-full rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-white dark:bg-slate-700 px-3 text-base dark:text-white';

/**
 * Cài đặt overlay bảng xếp hạng donate. Không lưu gì ở máy chủ: chọn kiểu nào thì link mang
 * kiểu đó, streamer chép link vào OBS. Bản xem trước dùng số thật của streamer.
 */
const TopDonateSetting: React.FC<{ userId?: string }> = ({ userId }) => {
  const [options, setOptions] = useState<TopDonateOptions>(DEFAULT_TOP_DONATE_OPTIONS);
  const [rows, setRows] = useState<TopDonateRow[]>([]);

  const { host, protocol } = window.location;
  const overlayUrl = `${protocol}//${host}/top-donate-screen/${userId}?${topDonateQuery(options)}`;

  // Đổi giao diện không cần tải lại số liệu.
  const { range, by, top } = options;
  useEffect(() => {
    if (!userId) return undefined;
    let alive = true;
    loadTopDonate(userId, { range, by, top, style: DEFAULT_TOP_DONATE_OPTIONS.style })
      .then((r) => alive && setRows(r))
      .catch(() => alive && setRows([]));
    return () => {
      alive = false;
    };
  }, [userId, range, by, top]);

  const patch = (p: Partial<TopDonateOptions>) => setOptions((prev) => ({ ...prev, ...p }));

  const copyOverlay = async () => {
    try {
      await navigator.clipboard.writeText(overlayUrl);
      toast.success('Đã sao chép link');
    } catch {
      toast.error('Trình duyệt chặn sao chép, hãy bôi đen link rồi chép tay.');
    }
  };

  const topChoices = Array.from(
    { length: TOP_DONATE_MAX - TOP_DONATE_MIN + 1 },
    (_, i) => TOP_DONATE_MIN + i
  );

  return (
    <div className="border rounded-xl border-[#DEE0E6] dark:border-slate-700 mt-[10px]">
      <p className="p-3 flex items-center gap-2 w-full text-[#18191E] dark:text-white font-semibold text-[22px] leading-[27px]">
        <Trophy size={20} className="text-blue-600" />
        Bảng xếp hạng donate trên live
      </p>

      <div className="w-full flex flex-col gap-3 bg-[#f6f7f8] dark:bg-dark2 p-3">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Chèn bảng những người donate nhiều nhất lên phiên live. Bảng tự cập nhật mỗi khi có người
          donate. Người chọn donate ẩn danh không có tên trong bảng.
        </p>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <label htmlFor="top-range" className="text-sm">
              Tính trong khoảng
            </label>
            <select
              id="top-range"
              value={options.range}
              onChange={(e) => patch({ range: e.target.value as TopDonateOptions['range'] })}
              className={SELECT_CLASS}
            >
              {TOP_DONATE_RANGES.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="top-by" className="text-sm">
              Xếp hạng
            </label>
            <select
              id="top-by"
              value={options.by}
              onChange={(e) => patch({ by: e.target.value as TopDonateOptions['by'] })}
              className={SELECT_CLASS}
            >
              {TOP_DONATE_BY.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="top-count" className="text-sm">
              Số người hiện
            </label>
            <select
              id="top-count"
              value={options.top}
              onChange={(e) => patch({ top: Number(e.target.value) })}
              className={SELECT_CLASS}
            >
              {topChoices.map((n) => (
                <option key={n} value={n}>
                  {n} người
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="top-style" className="text-sm">
              Giao diện
            </label>
            <select
              id="top-style"
              value={options.style}
              onChange={(e) => patch({ style: e.target.value as TopDonateOptions['style'] })}
              className={SELECT_CLASS}
            >
              {TOP_DONATE_STYLES.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {options.range === 'subathon' && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Bảng tính từ lúc bạn bấm bắt đầu subathon gần nhất và dừng khi subathon kết thúc. Chưa
            chạy subathon lần nào thì bảng tính từ 0 giờ hôm nay.
          </p>
        )}

        <div className="flex flex-col gap-1">
          <span className="text-sm">Xem trước bằng số thật của bạn</span>
          <div className="flex justify-center rounded-xl p-4" style={{ background: STAGE_BG }}>
            <div className="w-full max-w-[420px]">
              <TopDonateBoard
                title={topDonateTitle(options.range)}
                rows={rows}
                by={options.by}
                style={options.style}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <h2 className="text-sm">Link chèn vào OBS, thêm dưới dạng nguồn trang web</h2>
          <div className="flex items-center gap-2">
            <div className="flex h-12 min-w-0 flex-1 items-center rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-[#EBECF2] dark:bg-slate-700 px-3 text-base text-[#A1A5B6] dark:text-slate-400">
              <span className="w-full truncate">{overlayUrl}</span>
            </div>
            <button
              type="button"
              onClick={copyOverlay}
              title="Sao chép link"
              className="flex h-12 shrink-0 items-center gap-1 rounded-lg bg-[#39539E] px-4 text-white"
            >
              <Copy size={16} />
              Sao chép
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Đổi lựa chọn ở trên thì link đổi theo, nhớ chép lại link mới vào OBS.
          </p>
        </div>

        <div>
          <button
            type="button"
            onClick={() => window.open(overlayUrl, 'popup', 'width=480,height=520,top=100,left=100')}
            className="inline-flex items-center gap-2 rounded-lg bg-[#39539E] px-4 py-2 text-base text-white"
          >
            <ExternalLink size={16} />
            Mở thử
          </button>
        </div>
      </div>
    </div>
  );
};

export default TopDonateSetting;
